import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor, type RichTextEditorHandle } from "@/components/rich-text-editor";
import { CcEmailInput } from "@/components/cc-email-input";
import { Badge } from "@/components/ui/badge";
import { DripCampaignTab } from "@/components/drip-campaign-tab";
import { SmsCampaignTab } from "@/components/sms-campaign-tab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { describeSendError } from "@/lib/send-guard";
import {
  Loader2,
  Plus,
  Search,
  Mail,
  FileText,
  Zap,
  Send,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  Users,
  MapPin,
  Clock,
  Check,
  ChevronsUpDown,
  Settings,
  Ban,
  Droplets,
  MessageSquare,
  RefreshCw,
  BarChart3,
  TrendingUp,
  User,
  CalendarClock,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import { ScheduledEmailsList } from "@/components/scheduled-emails-list";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { VoiceToTextTextarea, VoiceToTextInput, VoiceToTextButton } from "@/components/voice-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn, getWorkspaceTimeZone, getTimeZoneAbbreviation } from "@/lib/utils";
import { Link, useLocation } from "wouter";
import type { UserRole, UserProfile, EmailTemplate, EmailAutomation, Region, Lead, Client, ConfirmationEmailTemplate, ClientTypeRecord, Talent, EventOffering, Campaign, CampaignRecipient } from "@shared/schema";
import { NO_CALENDAR_TEMPLATE_CLIENT_TYPE, DEFAULT_NO_CALENDAR_TEMPLATE } from "@shared/schema";
import { AIDraftButton } from "@/components/ai-draft-button";
import { sanitizeEmailHtml } from "@/lib/sanitize-html";
import {
  ColdWarmToggle,
  WarmVolumeAckDialog,
  type MassEmailSettingsLite,
} from "@/components/email/cold-warm-controls";

interface EmailHubPageProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
}

export default function EmailHubPage({ effectiveRole, effectiveUserId }: EmailHubPageProps) {
  const { user } = useAuth();
  const { permissions: effectivePermissions } = useEffectivePermissions();
  const coldEmailAllowed = effectivePermissions["communications.feature.coldEmail"] === true;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Read ?tab= and ?draftId= for deeplinks from notification hub blocked-send pings.
  const initialQuery = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const requestedTab = initialQuery?.get("tab") || "templates";
  // Inbox, Drafts, and Outbox all live in the dedicated Email Hub (/inbox)
  // now. Any deep link that still points at one of them is redirected to
  // the Email Hub on the right folder, preserving ?draftId for the outbox.
  const initialDraftId = initialQuery?.get("draftId") ?? null;
  const [, navigate] = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (requestedTab === "inbox" || requestedTab === "drafts" || requestedTab === "outbox") {
      const params = new URLSearchParams();
      params.set("folder", "outbox");
      if (initialDraftId) params.set("draftId", initialDraftId);
      // Client-side navigation through the workspace-scoped router so the
      // /w/:slug base is preserved. A hard window.location redirect to a bare
      // /inbox path drops the workspace prefix and bounces through login →
      // dashboard, losing the outbox/draft target (Task #477).
      navigate(`/inbox?${params.toString()}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialTab = (requestedTab === "inbox" || requestedTab === "drafts" || requestedTab === "outbox")
    ? "templates"
    : requestedTab;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  // Admin-only filter for the Master Templates list, narrowed by visibleToRoles.
  // "ALL" = no filter, "EVERYONE" = templates with empty visibleToRoles,
  // any UserRole value = templates whose visibleToRoles includes that role.
  const [masterVisibilityFilter, setMasterVisibilityFilter] = useState<"ALL" | "EVERYONE" | UserRole>("ALL");
  
  // Template dialog state
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const templateBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [templateTags, setTemplateTags] = useState("");
  const [templateScope, setTemplateScope] = useState<"STANDARD" | "USER">("USER");
  // Task #696: admin-only "hide from template pickers" toggle.
  const [templateHiddenFromPickers, setTemplateHiddenFromPickers] = useState(false);
  // Task #696: Templates Manager "Show hidden" filter. Default ON in the
  // manager so admins always see the full list and can manage the toggle;
  // pickers themselves never show hidden rows regardless of this state.
  const [showHiddenInManager, setShowHiddenInManager] = useState(true);
  // Role-based visibility for STANDARD (master) templates only.
  // Empty array = visible to all roles. Admins always see every master template.
  const [templateVisibleToRoles, setTemplateVisibleToRoles] = useState<UserRole[]>([]);
  
  // Automation dialog state
  const [isAutomationDialogOpen, setIsAutomationDialogOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<EmailAutomation | null>(null);
  const [automationName, setAutomationName] = useState("");
  const [automationClientTypeIds, setAutomationClientTypeIds] = useState<string[]>([]);
  const [automationTriggerType, setAutomationTriggerType] = useState("BEFORE_EVENT_DATE");
  const [automationTriggerOffset, setAutomationTriggerOffset] = useState("0");
  const [automationTriggerUnit, setAutomationTriggerUnit] = useState("DAYS");
  const [automationAssignedToUserId, setAutomationAssignedToUserId] = useState("");
  const [automationTemplateId, setAutomationTemplateId] = useState("");
  const [automationIsActive, setAutomationIsActive] = useState(true);
  const [automationSendTime, setAutomationSendTime] = useState("09:00");
  const [automationSendTimezone, setAutomationSendTimezone] = useState("CLIENT");
  const [automationRequireUnpaidInvoice, setAutomationRequireUnpaidInvoice] = useState(false);
  
  // Send tab state
  const [sendRegion, setSendRegion] = useState("");
  const [regionPopoverOpen, setRegionPopoverOpen] = useState(false);
  const [audiencePopoverOpen, setAudiencePopoverOpen] = useState(false);
  const [sendAudience, setSendAudience] = useState<string[]>([]);
  
  // Audience type options
  const audienceOptions = [
    { value: "all_clients", label: "All Clients" },
    { value: "all_leads", label: "All Leads" },
    { value: "interested_leads", label: "Interested Leads" },
    { value: "new_leads", label: "Unassigned Leads" },
    { value: "not_now_leads", label: "'Not Now' Leads" },
  ];

  // Task #685 — audience values that route through the cold-tier
  // sending pipeline. Mirror of `COLD_AUDIENCE_TYPES_DEFAULT` in
  // server/lib/email/cold-send-policy.ts (kept narrow to what the
  // Mass Email composer actually exposes).
  const COLD_AUDIENCE_VALUES = new Set([
    "all_leads",
    "new_leads",
    "not_now_leads",
    "interested_leads",
  ]);
  const [sendSubject, setSendSubject] = useState("");
  const [sendBody, setSendBody] = useState("");
  const sendBodyEditorRef = useRef<RichTextEditorHandle>(null);
  const [sendCc, setSendCc] = useState("");
  const [sendSelectedTemplate, setSendSelectedTemplate] = useState("");
  // Cold/warm sending mode for the mass composer. Lead audiences default to
  // cold; everything else defaults to warm (see the willUseColdTier effect).
  const [sendRiskTier, setSendRiskTier] = useState<"cold" | "warm">("cold");
  const [sendRiskTierTouched, setSendRiskTierTouched] = useState(false);
  const [warmAckOpen, setWarmAckOpen] = useState(false);
  const [warmAckInfo, setWarmAckInfo] = useState<{ count: number; threshold: number; prospectOverride: boolean } | null>(null);
  const [pendingWarmPayload, setPendingWarmPayload] = useState<any>(null);
  // Which flow opened the warm-volume ack dialog: an immediate send or a
  // deferred (scheduled) send. The dialog's confirm action branches on this.
  const [warmAckMode, setWarmAckMode] = useState<"send" | "schedule">("send");
  const [schedulingMass, setSchedulingMass] = useState(false);

  // Schedule a mass email for later. Runs the same warm-volume gate the
  // immediate send does: the backend returns requiresWarmVolumeConfirmation
  // (validate-only, nothing persisted) and we surface the checkbox modal,
  // then re-submit with confirmWarmHighVolume:true.
  async function submitScheduleMass(confirmWarm: boolean) {
    if (!scheduledDate || !scheduledTime) return;
    // Build the picker's instant in UTC, interpreting the wall-clock the
    // user typed as the workspace's configured timezone.
    const localCT = new Date(`${scheduledDate}T${scheduledTime}:00`);
    const ctParts: Record<string, string> = {};
    for (const p of new Intl.DateTimeFormat("en-US", {
      timeZone: getWorkspaceTimeZone(),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(localCT)) {
      if (p.type !== "literal") ctParts[p.type] = p.value;
    }
    const ctReadback = new Date(
      `${ctParts.year}-${ctParts.month}-${ctParts.day}T${ctParts.hour}:${ctParts.minute}:${ctParts.second}`,
    );
    const skewMs = localCT.getTime() - ctReadback.getTime();
    const scheduledForUtc = new Date(localCT.getTime() + skewMs);

    setSchedulingMass(true);
    try {
      const res = await apiRequest("POST", "/api/scheduled-emails", {
        scheduledFor: scheduledForUtc.toISOString(),
        sourceType: "mass_email",
        sourcePayload: {
          recipients: { region: sendRegion || null, audience: sendAudience },
          subject: sendSubject,
          body: sendBody,
          cc: sendCc?.trim() || undefined,
          riskTier: sendRiskTier,
          ...(confirmWarm ? { confirmWarmHighVolume: true } : {}),
        },
      });
      const result = await res.json();
      if (result?.requiresWarmVolumeConfirmation && !confirmWarm) {
        setWarmAckInfo({
          count: result.totalRecipients ?? 0,
          threshold: result.warmVolumeThreshold ?? 20,
          prospectOverride: !!result.prospectOverride,
        });
        setWarmAckMode("schedule");
        setWarmAckOpen(true);
        return;
      }
      toast({
        title: "Mass email scheduled",
        description: `Will send ${scheduledDate} at ${scheduledTime} CT.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-emails"] });
      setIsScheduleDialogOpen(false);
      setScheduledDate("");
      setScheduledTime("");
      setSendSubject("");
      setSendBody("");
      setSendCc("");
      setSendRegion("");
      setSendAudience([]);
      setWarmAckOpen(false);
      setWarmAckInfo(null);
    } catch (err: any) {
      toast({
        title: "Couldn't schedule",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSchedulingMass(false);
    }
  }
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"template" | "automation">("template");
  const [deleteId, setDeleteId] = useState("");

  // Campaign send confirmation modal state
  const [campaignConfirmOpen, setCampaignConfirmOpen] = useState(false);
  const [campaignCapacity, setCampaignCapacity] = useState<{
    dailyCap: number;
    graceBuffer: number;
    totalAllowed: number;
    campaignSentToday: number;
    remainingToday: number;
    sendWindowStart: string;
    sendWindowEnd: string;
    timezone: string;
    totalRecipients: number;
    willSendToday: number;
    queuedForLater: number;
  } | null>(null);
  const [isLoadingCapacity, setIsLoadingCapacity] = useState(false);

  // Confirmation email template state (admin only)
  const [confirmationClientType, setConfirmationClientType] = useState("Senior Living");
  const [confirmationTalentId, setConfirmationTalentId] = useState<string>("");
  const [confirmationEventOfferingId, setConfirmationEventOfferingId] = useState<string>("");
  const [confirmationSubject, setConfirmationSubject] = useState("");
  const [confirmationBody, setConfirmationBody] = useState("");
  const confirmationBodyEditorRef = useRef<RichTextEditorHandle>(null);

  // "Booked Without Calendar" tenant template state (admin only). Stored as a
  // standalone system template (clientType = talent_booking_no_calendar).
  const [noCalSubject, setNoCalSubject] = useState("");
  const [noCalBody, setNoCalBody] = useState("");
  const noCalBodyEditorRef = useRef<RichTextEditorHandle>(null);

  // Mass email settings state (admin only)
  const [massEmailOptOutPhrase, setMassEmailOptOutPhrase] = useState("");
  const [massEmailOptOutAudiences, setMassEmailOptOutAudiences] = useState<string[]>([]);
  const [massEmailLockForNonAdmins, setMassEmailLockForNonAdmins] = useState(true);

  // Fetch user profile for role
  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });

  // Use effectiveRole when viewing as another user, otherwise use profile role
  const isAdmin = effectiveRole ? effectiveRole === "admin" : profile?.role === "admin";

  // Fetch templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
  });

  // Surfaces preset-template seed failures recorded server-side so admins
  // know why canned templates are missing for their workspace and can retry
  // the seeder in-app. The banner component itself owns the query/mutation;
  // it is mirrored on the admin shell so admins who never visit Email Hub
  // still see the alert (see also admin.tsx).

  // Fetch automations
  const { data: automations = [], isLoading: automationsLoading } = useQuery<EmailAutomation[]>({
    queryKey: ["/api/email-automations"],
    enabled: isAdmin, // Only fetch for admins since automations are now admin-only
  });

  // Fetch regions for audience selection
  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
  });

  // Fetch confirmation email templates (admin only)
  const { data: confirmationTemplates = [], isLoading: confirmationTemplatesLoading } = useQuery<ConfirmationEmailTemplate[]>({
    queryKey: ["/api/confirmation-templates"],
    enabled: isAdmin,
  });

  // Fetch leads and clients for contacts selection
  const { data: leads = [] } = useQuery<Lead[]>({
    queryKey: ["/api/leads"],
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: clientTypesData = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  // Fetch talent for confirmation email templates
  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  // Fetch default talent
  const { data: defaultTalent } = useQuery<Talent>({
    queryKey: ["/api/talent/default"],
  });

  // Fetch users for admin assignment (admin only)
  const { data: allUsers = [] } = useQuery<UserProfile[]>({
    queryKey: ["/api/users"],
    enabled: isAdmin,
  });

  // Fetch show offerings for confirmation email templates
  const { data: eventOfferings = [] } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
  });

  // Fetch mass email settings (admin only)
  const massEmailSettingsQuery = useQuery<{
    id: string;
    optOutPhrase: string;
    optOutAudienceTypes: string[];
    lockOptOutForNonAdmins: boolean;
  }>({
    queryKey: ["/api/admin/mass-email-settings"],
    enabled: isAdmin,
  });

  // Fetch suppressed leads (admin only)
  const suppressedLeadsQuery = useQuery<Lead[]>({
    queryKey: ["/api/admin/suppressed-leads"],
    enabled: isAdmin,
  });

  const suppressedLeads = suppressedLeadsQuery.data || [];

  // Fetch campaign queue stats for Email Health dashboard (admin only)
  const queueStatsQuery = useQuery<{
    total: number;
    scheduledToday: number;
    scheduledFuture: number;
    byCampaign: Array<{ campaignId: string; campaignName: string; pendingCount: number }>;
  }>({
    queryKey: ["/api/admin/deliverability/queue-stats"],
    enabled: isAdmin,
    staleTime: 60000, // 1 minute stale time - manual refresh preferred to avoid modal interference
  });

  const queueStats = queueStatsQuery.data || { total: 0, scheduledToday: 0, scheduledFuture: 0, byCampaign: [] };

  // Fetch workspace-wide email summary stats (admin only)
  const workspaceSummaryQuery = useQuery<{
    totalEmailsSentToday: number;
    totalCapacityToday: number;
    capacityUsedPercent: number;
    automationEmails: { total: number; today: number };
    campaignEmails: { sent: number; failed: number; activeCampaigns: number };
    dripCampaigns: { active: number; total: number };
    engagement: { bounces: number; replies: number; optOuts: number };
    mailboxCount: number;
  }>({
    queryKey: ["/api/admin/deliverability/workspace-summary"],
    enabled: isAdmin,
    staleTime: 60000,
  });

  const workspaceSummary = workspaceSummaryQuery.data;

  // Fetch historical trending data (admin only)
  const trendingQuery = useQuery<{
    days: number;
    startDate: string;
    endDate: string;
    dailyStats: Array<{
      date: string;
      campaignSent: number;
      individualSent: number;
      totalSent: number;
      bounces: number;
      replies: number;
      optOuts: number;
      capacity: number;
    }>;
    totals: {
      sent: number;
      campaignSent: number;
      individualSent: number;
      bounces: number;
      replies: number;
      optOuts: number;
    };
  }>({
    queryKey: ["/api/admin/deliverability/trending", { days: 30 }],
    enabled: isAdmin,
    staleTime: 300000, // 5 minutes
  });

  const trendingData = trendingQuery.data;

  // Fetch all mailbox settings for detailed view (admin only)
  const allMailboxesQuery = useQuery<Array<{
    mailboxId: string;
    warmupStartDate: Date | null;
    graceBuffer: number;
    sendWindowStartLocal: string;
    sendWindowEndLocal: string;
    sendWindowTimezone: string;
    status: string;
    dailyCap: number;
    todayStats: {
      campaignSentCount: number;
      individualSentCount: number;
      hardBounceCount: number;
      softBounceCount: number;
      optOutCount: number;
      replyCount: number;
      failedSendCount: number;
    };
  }>>({
    queryKey: ["/api/admin/deliverability/all-mailboxes"],
    enabled: isAdmin,
    staleTime: 60000,
  });

  const allMailboxes = allMailboxesQuery.data || [];

  // Per-email breakdown
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | null>(null);

  type MailboxPeriodStats = { sent: number; campaignSent: number; individualSent: number; bounces: number; replies: number; optOuts: number; failed: number };
  type MailboxTimeline = {
    mailboxId: string;
    dailyCap: number;
    graceBuffer: number;
    settings: { status: string; sendWindowStart: string; sendWindowEnd: string; campaignEnabled: boolean } | null;
    periods: { yesterday: MailboxPeriodStats; thisWeek: MailboxPeriodStats; thisMonth: MailboxPeriodStats; thisYear: MailboxPeriodStats };
    upcoming: { total: number; today: number; thisWeek: number; future: number };
    dailyTrend: Array<{ date: string; sent: number; bounces: number; replies: number }>;
  };

  const mailboxTimelineQuery = useQuery<MailboxTimeline>({
    queryKey: ["/api/admin/deliverability/mailbox-timeline", selectedMailboxId],
    enabled: !!selectedMailboxId && isAdmin,
    staleTime: 60000,
  });
  const mailboxTimeline = mailboxTimelineQuery.data;

  // Fetch mass email campaigns for activity section
  const { data: campaigns = [], refetch: refetchCampaigns } = useQuery<Campaign[]>({
    queryKey: ["/api/campaigns"],
  });

  // Task #685 — cold-purpose sending domain (rep-readable, minimal fields).
  type ColdSendingDomain = {
    id: string;
    domain: string;
    fromAddress: string | null;
    spfVerified: boolean;
    dkimVerified: boolean;
    verificationStatus: string;
  };
  const willUseColdTier = sendAudience.some(v => COLD_AUDIENCE_VALUES.has(v));

  // Workspace warm-volume settings (threshold + override lock).
  const { data: massEmailSettings } = useQuery<MassEmailSettingsLite>({
    queryKey: ["/api/mass-email-settings"],
  });
  const warmThreshold = Math.max(1, massEmailSettings?.warmVolumeWarnThreshold ?? 20);
  const overrideLocked = !!massEmailSettings?.lockColdOverrideForNonAdmins && !isAdmin;

  // Default the toggle: lead/prospect audiences => cold, everything else =>
  // warm. Only auto-set until the user manually flips it.
  useEffect(() => {
    if (sendRiskTierTouched) return;
    setSendRiskTier(willUseColdTier ? "cold" : "warm");
  }, [willUseColdTier, sendRiskTierTouched]);

  // If the workspace locks overrides for non-admins on a lead audience, force
  // back to cold (server enforces too — this is UX only).
  useEffect(() => {
    if (overrideLocked && willUseColdTier && sendRiskTier === "warm") {
      setSendRiskTier("cold");
    }
  }, [overrideLocked, willUseColdTier, sendRiskTier]);
  // Feature Visibility gate: when "Cold Email Mode" is hidden for this role/tier,
  // force warm so the cold sending path is never selectable from the client.
  useEffect(() => {
    if (!coldEmailAllowed && sendRiskTier === "cold") {
      setSendRiskTier("warm");
    }
  }, [coldEmailAllowed, sendRiskTier]);
  const coldSendingDomainQuery = useQuery<{ domain: ColdSendingDomain | null }>({
    queryKey: ["/api/sending-domains/cold"],
    staleTime: 60_000,
    // Only hit the backend when the rep has actually picked a
    // cold-tier audience — no point paying for the round-trip
    // otherwise.
    enabled: willUseColdTier,
  });
  const coldSendingDomain = coldSendingDomainQuery.data?.domain ?? null;
  const coldDomainQueryReady = coldSendingDomainQuery.isSuccess;
  const coldDomainQueryFailed = coldSendingDomainQuery.isError;
  const coldDomainVerified = !!(coldSendingDomain && coldSendingDomain.spfVerified && coldSendingDomain.dkimVerified);

  // Fetch available email senders for the workspace
  type AvailableSender = { id: string; email: string; name: string; type: 'connector' | 'user' };
  const { data: availableSenders = [] } = useQuery<AvailableSender[]>({
    queryKey: ["/api/available-senders"],
  });

  // State for viewing campaign details
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [isCampaignDetailOpen, setIsCampaignDetailOpen] = useState(false);

  // Fetch selected campaign with recipients
  const { data: selectedCampaignData, isLoading: isLoadingCampaignDetail } = useQuery<Campaign & { recipients: CampaignRecipient[] }>({
    queryKey: ["/api/campaigns", selectedCampaignId],
    enabled: !!selectedCampaignId && isCampaignDetailOpen,
  });

  // "Edit & retry failed recipients" state for the campaign detail dialog.
  // We keep this state local to the dialog so the user can flip into edit
  // mode, fix the merge variable that broke their send, and re-queue the
  // FAILED recipients without recreating the campaign from scratch.
  const [isEditingRetry, setIsEditingRetry] = useState(false);
  const [retryEditSubject, setRetryEditSubject] = useState("");
  const [retryEditBody, setRetryEditBody] = useState("");
  const [retryGuardError, setRetryGuardError] = useState<string[] | null>(null);
  useEffect(() => {
    if (selectedCampaignData && !isEditingRetry) {
      setRetryEditSubject(selectedCampaignData.subject || "");
      setRetryEditBody(selectedCampaignData.body || "");
    }
  }, [selectedCampaignData?.id, isEditingRetry]);
  // Reset edit state whenever the dialog opens against a different campaign.
  useEffect(() => {
    if (!isCampaignDetailOpen) {
      setIsEditingRetry(false);
      setRetryGuardError(null);
    }
  }, [isCampaignDetailOpen, selectedCampaignId]);

  const retryFailedCampaignMutation = useMutation({
    mutationFn: async (payload: { id: string; subject?: string; body?: string }) => {
      const response = await apiRequest("POST", `/api/campaigns/${payload.id}/retry-failed`, {
        subject: payload.subject,
        body: payload.body,
      });
      return response.json();
    },
    onSuccess: (data: any) => {
      setIsEditingRetry(false);
      setRetryGuardError(null);
      toast({ title: "Retry queued", description: data?.message || "Failed recipients re-queued." });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns", selectedCampaignId] });
    },
    onError: (error: any) => {
      const { details, title, description } = describeSendError(error, "Failed to retry campaign.");
      if (details.isGuardError && details.tokens?.length) {
        setRetryGuardError(details.tokens);
      } else {
        setRetryGuardError(null);
      }
      toast({ title, description, variant: "destructive" });
    },
  });

  // Update mass email settings state when data loads
  useEffect(() => {
    if (massEmailSettingsQuery.data) {
      setMassEmailOptOutPhrase(massEmailSettingsQuery.data.optOutPhrase || "");
      setMassEmailOptOutAudiences(massEmailSettingsQuery.data.optOutAudienceTypes || []);
      setMassEmailLockForNonAdmins(massEmailSettingsQuery.data.lockOptOutForNonAdmins ?? true);
    }
  }, [massEmailSettingsQuery.data]);

  const activeTalent = talent.filter(e => e.isActive);
  const activeEventOfferings = eventOfferings;

  // Set default show offering for confirmation templates when data loads
  useEffect(() => {
    if (eventOfferings.length > 0 && !confirmationEventOfferingId) {
      // First try to find the default offering (is_default = true)
      const defaultOffering = eventOfferings.find(o => o.isDefault);
      if (defaultOffering) {
        setConfirmationEventOfferingId(defaultOffering.id);
      } else {
        // Fallback: find one containing "Full Show" in the name
        const fullEventOffering = eventOfferings.find(o => 
          o.name.toLowerCase().includes("full show")
        );
        if (fullEventOffering) {
          setConfirmationEventOfferingId(fullEventOffering.id);
        } else if (eventOfferings.length > 0) {
          // Ultimate fallback: use the first offering
          setConfirmationEventOfferingId(eventOfferings[0].id);
        }
      }
    }
  }, [eventOfferings, confirmationEventOfferingId]);

  const clientTypeOptions = clientTypesData
    .filter(ct => ct.isActive)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  // Separate templates by scope
  const standardTemplates = templates.filter(t => t.scope === "STANDARD");
  // Use effectiveUserId when viewing as another user
  const activeUserId = effectiveUserId || user?.id;
  const myTemplates = templates.filter(t => t.scope === "USER" && t.ownerUserId === activeUserId);
  const adminPrivateTemplates = isAdmin ? templates.filter(t => t.scope === "ADMIN_PRIVATE" && t.ownerUserId === activeUserId) : [];
  const allUserTemplates = isAdmin ? templates.filter(t => t.scope === "USER") : [];

  // Separate automations by scope
  const standardAutomations = automations.filter(a => a.scope === "STANDARD");
  const myAutomations = automations.filter(a => a.scope === "USER" && a.ownerUserId === activeUserId);
  const adminPrivateAutomations = isAdmin ? automations.filter(a => a.scope === "ADMIN_PRIVATE" && a.ownerUserId === activeUserId) : [];
  const allUserAutomations = isAdmin ? automations.filter(a => a.scope === "USER") : [];

  // Filter by search query
  const filterBySearch = <T extends { name: string }>(items: T[]) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Template mutations
  const createTemplateMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/email-templates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      toast({ title: "Template created", description: "Your email template has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create template.", variant: "destructive" });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return apiRequest("PATCH", `/api/email-templates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      toast({ title: "Template updated", description: "Your email template has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update template.", variant: "destructive" });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/email-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      setDeleteConfirmOpen(false);
      toast({ title: "Template deleted", description: "The email template has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete template.", variant: "destructive" });
    },
  });

  // Automation mutations
  const createAutomationMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/email-automations", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setIsAutomationDialogOpen(false);
      resetAutomationForm();
      toast({ title: "Automation created", description: "Your email automation has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create automation.", variant: "destructive" });
    },
  });

  const updateAutomationMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return apiRequest("PATCH", `/api/email-automations/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setIsAutomationDialogOpen(false);
      resetAutomationForm();
      toast({ title: "Automation updated", description: "Your email automation has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update automation.", variant: "destructive" });
    },
  });

  const deleteAutomationMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/email-automations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setDeleteConfirmOpen(false);
      toast({ title: "Automation deleted", description: "The email automation has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete automation.", variant: "destructive" });
    },
  });

  const duplicateAutomationMutation = useMutation({
    mutationFn: async (automation: EmailAutomation) => {
      const { id, createdAt, updatedAt, ...rest } = automation as any;
      return apiRequest("POST", "/api/email-automations", {
        ...rest,
        name: `${automation.name} (Copy)`,
        isActive: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      toast({ title: "Automation duplicated", description: "A copy has been created and set to inactive." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to duplicate automation.", variant: "destructive" });
    },
  });

  // Send bulk email mutation
  const sendBulkEmailMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/email/bulk-send", data);
      return res.json();
    },
    onSuccess: (result: any, variables: any) => {
      // Warm-volume acknowledge-gate: the runner held the send and is asking
      // for a deliberate confirmation. Open the checkbox modal and stash the
      // payload so "Send anyway" can re-submit with confirmWarmHighVolume.
      if (result.requiresWarmVolumeConfirmation && !variables?.confirmWarmHighVolume) {
        setWarmAckInfo({
          count: result.totalRecipients ?? 0,
          threshold: result.warmVolumeThreshold ?? warmThreshold,
          prospectOverride: !!result.prospectOverride,
        });
        setPendingWarmPayload(variables);
        setWarmAckOpen(true);
        setCampaignConfirmOpen(false);
        return;
      }
      // Bounce-risk gate (cold tier): confirm then re-submit.
      if (result.requiresConfirmation && !variables?.confirmHighBounceRisk) {
        const pct = result.estimatedBounceRate ? `${(result.estimatedBounceRate * 100).toFixed(1)}%` : "elevated";
        setCampaignConfirmOpen(false);
        if (window.confirm(`Projected bounce rate is ${pct}. Send anyway?`)) {
          sendBulkEmailMutation.mutate({ ...variables, confirmHighBounceRisk: true });
        }
        return;
      }
      if (result.blocked) {
        toast({ title: "Send blocked", description: result.blockedReason || result.message, variant: "destructive" });
        return;
      }
      // Check if this is a queued result (from new campaign system) or immediate send (legacy)
      if (result.campaignId || result.willSendToday !== undefined) {
        const willSendToday = result.willSendToday || result.sentCount || 0;
        const queuedLater = result.queuedForLater || 0;
        const skippedUnverified = result.skippedUnverified || 0;
        toast({
          title: "Emails queued",
          description: `Queued ${result.totalRecipients || willSendToday + queuedLater}. Sending ${willSendToday} today${queuedLater > 0 ? `, ${queuedLater} starting next business day` : ""}.${skippedUnverified > 0 ? ` ${skippedUnverified} skipped as undeliverable (verification).` : ""}`,
        });
      } else {
        toast({
          title: "Emails sent",
          description: `Successfully sent ${result.sentCount} emails.${result.failedCount > 0 ? ` ${result.failedCount} failed.` : ""}`,
        });
      }
      setSendSubject("");
      setSendBody("");
      setSendCc("");
      setSendSelectedTemplate("");
      setWarmAckOpen(false);
      setWarmAckInfo(null);
      setPendingWarmPayload(null);
      setSendRiskTierTouched(false);
      // Refresh Campaign Activity list and Email Health dashboard
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/deliverability/queue-stats"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to send emails.", variant: "destructive" });
    },
  });

  // Confirmation email template mutation (admin only)
  const saveConfirmationTemplateMutation = useMutation({
    mutationFn: async (data: { clientType: string; talentId?: string | null; eventOfferingId?: string | null; subject: string; body: string; isActive: boolean }) => {
      return apiRequest("POST", "/api/confirmation-templates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/confirmation-templates"] });
      toast({ title: "Template saved", description: "Confirmation email template has been saved." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save template.", variant: "destructive" });
    },
  });

  // Mass email settings mutation (admin only)
  const saveMassEmailSettingsMutation = useMutation({
    mutationFn: async (data: { optOutPhrase: string; optOutAudienceTypes: string[]; lockOptOutForNonAdmins: boolean }) => {
      return apiRequest("PATCH", "/api/admin/mass-email-settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/mass-email-settings"] });
      toast({ title: "Settings saved", description: "Mass email settings have been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save settings.", variant: "destructive" });
    },
  });

  // Unsuppress lead mutation (admin only)
  const unsuppressLeadMutation = useMutation({
    mutationFn: async (leadId: string) => {
      return apiRequest("POST", `/api/admin/leads/${leadId}/unsuppress`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/suppressed-leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Lead unsuppressed", description: "The lead has been unsuppressed and can receive emails again." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to unsuppress lead.", variant: "destructive" });
    },
  });

  const handleSaveMassEmailSettings = () => {
    saveMassEmailSettingsMutation.mutate({
      optOutPhrase: massEmailOptOutPhrase,
      optOutAudienceTypes: massEmailOptOutAudiences,
      lockOptOutForNonAdmins: massEmailLockForNonAdmins,
    });
  };

  const handleUnsuppressLead = (leadId: string) => {
    unsuppressLeadMutation.mutate(leadId);
  };

  // Load selected confirmation template when client type, talent, or show offering changes
  const selectedConfirmationTemplate = confirmationTemplates.find(t => 
    t.clientType === confirmationClientType && 
    (t.talentId === confirmationTalentId || (!t.talentId && !confirmationTalentId)) &&
    (t.eventOfferingId === confirmationEventOfferingId || (!t.eventOfferingId && !confirmationEventOfferingId))
  );

  // Default confirmation email templates by client type
  const getDefaultConfirmationTemplate = (clientType: string) => {
    const defaults: Record<string, { subject: string; body: string }> = {
      "Senior Living": {
        subject: "Event Confirmed - {{client_name}}",
        body: `Dear {{contact_name}},

Thank you for booking with us! We're excited to bring our event to your senior living community.

Event Details:
- Date: {{date}}
- Time: {{event_time}}
- Location: {{location}}
- Event: {{booking_details}}

We will arrive 30 minutes before the scheduled performance for setup.

If you have any questions or need to make changes, please don't hesitate to contact us.

Best regards,
{{sender_name}}`,
      },
      "Corporate": {
        subject: "Corporate Event Confirmation - {{client_name}}",
        body: `Dear {{contact_name}},

Thank you for choosing us for your corporate event!

Event Details:
- Date: {{date}}
- Time: {{event_time}}
- Location: {{location}}
- Event: {{booking_details}}

We will arrive 45 minutes before the scheduled performance for setup and sound check.

Please ensure we have access to the venue and any required A/V equipment as discussed.

Looking forward to a great event!

Best regards,
{{sender_name}}`,
      },
      "Student Org": {
        subject: "Event Confirmed - {{client_name}}",
        body: `Dear {{contact_name}},

Thank you for booking with us! We're excited to bring our event to your student organization.

Event Details:
- Date: {{date}}
- Time: {{event_time}}
- Location: {{location}}
- Event: {{booking_details}}

We will arrive 30 minutes before the scheduled performance for setup.

If you have any questions or need to make changes, please don't hesitate to contact us.

Best regards,
{{sender_name}}`,
      },
      "Private": {
        subject: "Your Private Event is Confirmed! - {{client_name}}",
        body: `Dear {{contact_name}},

We're thrilled to confirm your private event booking!

Event Details:
- Date: {{date}}
- Time: {{event_time}}
- Location: {{location}}
- Event: {{booking_details}}

We will arrive 30 minutes before the scheduled performance.

If you have any special requests or questions, please feel free to reach out.

We look forward to making your event memorable!

Best regards,
{{sender_name}}`,
      },
      "Other": {
        subject: "Event Confirmation - {{client_name}}",
        body: `Dear {{contact_name}},

This email confirms your event booking.

Event Details:
- Date: {{date}}
- Time: {{event_time}}
- Location: {{location}}
- Event: {{booking_details}}

We will arrive 30 minutes before the scheduled performance for setup.

If you have any questions, please don't hesitate to reach out.

Best regards,
{{sender_name}}`,
      },
    };
    return defaults[clientType] || defaults["Senior Living"];
  };

  // Update confirmation template form when client type, talent, or show offering changes
  useEffect(() => {
    if (isAdmin && !confirmationTemplatesLoading) {
      // First try to find a template matching all three: client type, talent, and show offering
      let template = confirmationTemplates.find(t => 
        t.clientType === confirmationClientType && 
        t.talentId === confirmationTalentId &&
        t.eventOfferingId === confirmationEventOfferingId
      );
      
      // Try client type + talent (no show offering)
      if (!template && confirmationTalentId) {
        template = confirmationTemplates.find(t => 
          t.clientType === confirmationClientType && 
          t.talentId === confirmationTalentId &&
          !t.eventOfferingId
        );
      }
      
      // Try client type + show offering (no talent)
      if (!template && confirmationEventOfferingId) {
        template = confirmationTemplates.find(t => 
          t.clientType === confirmationClientType && 
          !t.talentId &&
          t.eventOfferingId === confirmationEventOfferingId
        );
      }
      
      // Fall back to template with just client type
      if (!template) {
        template = confirmationTemplates.find(t => 
          t.clientType === confirmationClientType && 
          !t.talentId &&
          !t.eventOfferingId
        );
      }
      
      if (template) {
        setConfirmationSubject(template.subject);
        setConfirmationBody(template.body);
      } else {
        // Load default template for current client type
        const defaultTemplate = getDefaultConfirmationTemplate(confirmationClientType);
        setConfirmationSubject(defaultTemplate.subject);
        setConfirmationBody(defaultTemplate.body);
      }
    }
  }, [isAdmin, confirmationTemplatesLoading, confirmationTemplates, confirmationClientType, confirmationTalentId, confirmationEventOfferingId]);

  // Load the tenant "Booked Without Calendar" template, falling back to the
  // shared default copy when the workspace hasn't customized it yet.
  useEffect(() => {
    if (isAdmin && !confirmationTemplatesLoading) {
      const noCal = confirmationTemplates.find(t => t.clientType === NO_CALENDAR_TEMPLATE_CLIENT_TYPE);
      setNoCalSubject(noCal?.subject ?? DEFAULT_NO_CALENDAR_TEMPLATE.subject);
      setNoCalBody(noCal?.body ?? DEFAULT_NO_CALENDAR_TEMPLATE.body);
    }
  }, [isAdmin, confirmationTemplatesLoading, confirmationTemplates]);

  // Set default talent when loaded
  useEffect(() => {
    if (defaultTalent && !confirmationTalentId) {
      setConfirmationTalentId(defaultTalent.id);
    }
  }, [defaultTalent, confirmationTalentId]);

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateName("");
    setTemplateSubject("");
    setTemplateBody("");
    setTemplateTags("");
    setTemplateScope("USER");
    setTemplateVisibleToRoles([]);
    setTemplateHiddenFromPickers(false);
  };

  const resetAutomationForm = () => {
    setEditingAutomation(null);
    setAutomationName("");
    setAutomationTriggerType("BEFORE_EVENT_DATE");
    setAutomationTriggerOffset("0");
    setAutomationTriggerUnit("DAYS");
    setAutomationTemplateId("");
    setAutomationIsActive(true);
    setAutomationAssignedToUserId(activeUserId || "");
    setAutomationSendTime("09:00");
    setAutomationSendTimezone("CLIENT");
    setAutomationRequireUnpaidInvoice(false);
    setAutomationClientTypeIds([]);
  };

  const openEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateSubject(template.subject);
    setTemplateBody(template.body);
    setTemplateTags(template.tags || "");
    setTemplateScope(template.scope as "STANDARD" | "USER" || "USER");
    setTemplateVisibleToRoles(((template.visibleToRoles as UserRole[] | null) || []));
    setTemplateHiddenFromPickers(!!template.hiddenFromPickers);
    setIsTemplateDialogOpen(true);
  };

  const openEditAutomation = (automation: EmailAutomation) => {
    setEditingAutomation(automation);
    setAutomationName(automation.name);
    setAutomationTriggerType(automation.triggerType);
    setAutomationTriggerOffset(String(automation.triggerOffsetValue));
    setAutomationTriggerUnit(automation.triggerOffsetUnit);
    setAutomationTemplateId(automation.templateId);
    setAutomationIsActive(automation.isActive ?? true);
    setAutomationAssignedToUserId(automation.assignedToUserId || "");
    setAutomationSendTime(automation.sendTime || "09:00");
    setAutomationSendTimezone(automation.sendTimezone || "CLIENT");
    setAutomationRequireUnpaidInvoice(automation.requireUnpaidInvoice ?? false);
    setAutomationClientTypeIds(((automation as any).clientTypeIds as string[] | null | undefined) || []);
    setIsAutomationDialogOpen(true);
  };

  const handleSaveTemplate = () => {
    const effectiveScope = isAdmin ? templateScope : "USER";
    const data: any = {
      name: templateName,
      subject: templateSubject,
      body: templateBody,
      tags: templateTags || null,
      scope: effectiveScope,
      ownerUserId: effectiveScope === "STANDARD" ? null : user?.id,
    };

    // visibleToRoles is admin-only and only meaningful for STANDARD templates.
    // The backend strips it otherwise; we still scope the payload here.
    if (isAdmin && effectiveScope === "STANDARD") {
      data.visibleToRoles = templateVisibleToRoles;
    }

    // Task #696: hiddenFromPickers is admin-only. The backend strips it for
    // non-admins; we still scope the payload here so we don't send misleading
    // values from the form state when the current user isn't an admin.
    if (isAdmin) {
      data.hiddenFromPickers = templateHiddenFromPickers;
    }

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  const handleSaveAutomation = () => {
    const data = {
      name: automationName,
      triggerType: automationTriggerType,
      triggerOffsetValue: parseInt(automationTriggerOffset) || 0,
      triggerOffsetUnit: automationTriggerUnit,
      audienceType: "CLIENT", // Always CLIENT now
      audienceValue: null,
      templateId: automationTemplateId,
      isActive: automationIsActive,
      sendTime: automationSendTime,
      sendTimezone: automationSendTimezone,
      requireUnpaidInvoice: automationRequireUnpaidInvoice,
      scope: "ADMIN_PRIVATE", // Admin-only automations
      ownerUserId: user?.id,
      assignedToUserId: automationAssignedToUserId && automationAssignedToUserId !== "none" ? automationAssignedToUserId : null,
      clientTypeIds: automationClientTypeIds,
    };

    if (editingAutomation) {
      updateAutomationMutation.mutate({ id: editingAutomation.id, data });
    } else {
      createAutomationMutation.mutate(data);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteType === "template") {
      deleteTemplateMutation.mutate(deleteId);
    } else {
      deleteAutomationMutation.mutate(deleteId);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSendSubject(template.subject);
      setSendBody(template.body);
      setSendSelectedTemplate(templateId);
      // Switch to Send tab when template is selected from Templates tab
      setActiveTab("send");
    }
  };

  // Open campaign confirmation modal - calculate capacity and recipient count
  const handleOpenCampaignConfirm = async () => {
    setIsLoadingCapacity(true);
    try {
      // Get the mailbox ID (current user's Gmail connected email)
      const mailboxId = profile?.gmailConnectedEmail || activeUserId || "default";
      
      // Fetch capacity info
      const capacityRes = await fetch(`/api/deliverability/capacity/${encodeURIComponent(mailboxId)}`, {
        credentials: "include",
      });
      const capacity = await capacityRes.json();
      
      // Calculate total recipients based on audience selection
      let totalRecipients = 0;
      const regionFilter = sendRegion || null;
      
      for (const audience of sendAudience) {
        if (audience === "all_clients") {
          const filtered = regionFilter 
            ? clients.filter(c => c.regionId === regionFilter && c.emailAddress)
            : clients.filter(c => c.emailAddress);
          totalRecipients += filtered.length;
        } else if (audience === "all_leads") {
          const filtered = regionFilter 
            ? leads.filter(l => l.regionId === regionFilter && l.emailAddress && !l.isSuppressed)
            : leads.filter(l => l.emailAddress && !l.isSuppressed);
          totalRecipients += filtered.length;
        } else if (audience === "interested_leads") {
          const filtered = regionFilter 
            ? leads.filter(l => l.regionId === regionFilter && l.emailAddress && !l.isSuppressed && l.status === "interested")
            : leads.filter(l => l.emailAddress && !l.isSuppressed && l.status === "interested");
          totalRecipients += filtered.length;
        } else if (audience === "new_leads") {
          const filtered = regionFilter 
            ? leads.filter(l => l.regionId === regionFilter && l.emailAddress && !l.isSuppressed && !l.rep)
            : leads.filter(l => l.emailAddress && !l.isSuppressed && !l.rep);
          totalRecipients += filtered.length;
        } else if (audience === "not_now_leads") {
          const filtered = regionFilter 
            ? leads.filter(l => l.regionId === regionFilter && l.emailAddress && !l.isSuppressed && l.status === "not_now")
            : leads.filter(l => l.emailAddress && !l.isSuppressed && l.status === "not_now");
          totalRecipients += filtered.length;
        }
      }
      
      const remainingToday = capacity.remainingToday || 0;
      const willSendToday = Math.min(totalRecipients, Math.max(0, remainingToday));
      const queuedForLater = Math.max(0, totalRecipients - Math.max(0, remainingToday));
      
      setCampaignCapacity({
        dailyCap: capacity.dailyCap,
        graceBuffer: capacity.graceBuffer,
        totalAllowed: capacity.totalAllowed,
        campaignSentToday: capacity.campaignSentToday,
        remainingToday: capacity.remainingToday,
        sendWindowStart: capacity.sendWindowStart,
        sendWindowEnd: capacity.sendWindowEnd,
        timezone: capacity.timezone,
        totalRecipients,
        willSendToday,
        queuedForLater,
      });
      
      setCampaignConfirmOpen(true);
    } catch (error) {
      console.error("Error fetching capacity:", error);
      toast({
        title: "Error",
        description: "Failed to calculate sending capacity. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCapacity(false);
    }
  };
  
  // Confirm and send bulk email (now routes through queue)
  const handleConfirmCampaignSend = () => {
    const recipientData = {
      region: sendRegion,
      audience: sendAudience,
    };

    sendBulkEmailMutation.mutate({
      recipients: recipientData,
      subject: sendSubject,
      body: sendBody,
      cc: sendCc.trim() || undefined,
      riskTier: sendRiskTier,
    });
    
    setCampaignConfirmOpen(false);
  };

  const handleSendBulkEmail = () => {
    // Open the confirmation modal instead of sending directly
    handleOpenCampaignConfirm();
  };

  const canEditTemplate = (template: EmailTemplate) => {
    if (isAdmin) return true;
    return template.scope === "USER" && template.ownerUserId === activeUserId;
  };

  const canEditAutomation = (automation: EmailAutomation) => {
    if (isAdmin) return true;
    return automation.scope === "USER" && automation.ownerUserId === activeUserId;
  };

  // Role options for the "Visible to" multi-select on STANDARD templates.
  // Admins always see every master template, so they're not included as a toggle.
  const VISIBILITY_ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
    { value: "sales_rep", label: "Sales Rep" },
    { value: "artist", label: "Talent" },
    { value: "billing", label: "Billing" },
    // v2: { value: "marketing", label: "Marketing" },
  ];
  const ROLE_LABEL_MAP: Record<string, string> = {
    sales_rep: "Sales Rep",
    artist: "Talent",
    billing: "Billing",
    admin: "Admin",
    marketing: "Marketing",
  };
  // Mirror of server's ALL_USER_ROLES so the UI never displays a stale or
  // unknown role name even if the API ever returns something we don't know.
  const KNOWN_VISIBILITY_ROLES = new Set<string>([
    "sales_rep",
    "artist",
    "billing",
    "admin",
    "marketing",
  ]);
  const formatVisibilityLabel = (roles: string[] | null | undefined): string => {
    const list = (roles || [])
      .filter((r): r is string => typeof r === "string" && KNOWN_VISIBILITY_ROLES.has(r));
    if (list.length === 0) return "Everyone";
    return list.map(r => ROLE_LABEL_MAP[r] || r).join(", ");
  };

  const TemplateCard = ({ template, showOwner = false }: { template: EmailTemplate; showOwner?: boolean }) => (
    <Card className="hover-elevate">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-base flex items-center gap-2">
              {template.name}
              {template.scope === "STANDARD" && !isAdmin && (
                <Lock className="w-3 h-3 text-muted-foreground" />
              )}
            </CardTitle>
            {(() => {
              // Show "Visible to" badge on master templates so admins can tell at
              // a glance who sees what. Admin-only: non-admins already cannot see
              // hidden master templates, so the column would just be noise for them.
              const showVisibility = isAdmin && template.scope === "STANDARD";
              const visibilityLabel = showVisibility
                ? formatVisibilityLabel((template.visibleToRoles as string[] | null) || [])
                : null;
              const tagList = template.tags ? template.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
              if (!visibilityLabel && tagList.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1 mt-1">
                  {visibilityLabel && (
                    <Badge
                      variant="outline"
                      className="text-xs flex items-center gap-1"
                      data-testid={`badge-visibility-${template.id}`}
                    >
                      <Eye className="w-3 h-3" />
                      <span className="text-muted-foreground">Visible to:</span>
                      <span>{visibilityLabel}</span>
                    </Badge>
                  )}
                  {/* Task #696: surface hidden state to admins so they can
                      tell at a glance which master rows are filtered out of
                      every picker. Non-admins shouldn't ever see this badge —
                      they can't toggle it and only see master templates that
                      visibleToRoles already permits. */}
                  {isAdmin && template.hiddenFromPickers && (
                    <Badge
                      variant="outline"
                      className="text-xs flex items-center gap-1 border-amber-300 text-amber-700 dark:text-amber-300"
                      data-testid={`badge-hidden-${template.id}`}
                    >
                      <EyeOff className="w-3 h-3" />
                      Hidden
                    </Badge>
                  )}
                  {tagList.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              );
            })()}
          </div>
          {canEditTemplate(template) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`template-menu-${template.id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditTemplate(template)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {/* Task #696: admin-only inline toggle so admins can flip
                    hidden-from-pickers without opening the edit dialog. We
                    intentionally re-fetch the row from the existing list and
                    PATCH only the boolean — keeps subject/body untouched. */}
                {isAdmin && (
                  <DropdownMenuItem
                    onClick={() => {
                      updateTemplateMutation.mutate({
                        id: template.id,
                        data: { hiddenFromPickers: !template.hiddenFromPickers },
                      });
                    }}
                    data-testid={`template-toggle-hidden-${template.id}`}
                  >
                    {template.hiddenFromPickers ? (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Show in pickers
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" />
                        Hide from pickers
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    setTemplateName(template.name + " (Copy)");
                    setTemplateSubject(template.subject);
                    setTemplateBody(template.body);
                    setTemplateTags(template.tags || "");
                    setEditingTemplate(null);
                    setIsTemplateDialogOpen(true);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => {
                    setDeleteType("template");
                    setDeleteId(template.id);
                    setDeleteConfirmOpen(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground line-clamp-2">{template.subject}</p>
        {showOwner && template.ownerUserId && (
          <p className="text-xs text-muted-foreground mt-2">Created by: {template.createdBy}</p>
        )}
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTemplateSelect(template.id)}
            data-testid={`use-template-${template.id}`}
          >
            <Mail className="w-4 h-4 mr-1" />
            Use Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const AutomationCard = ({ automation, showOwner = false }: { automation: EmailAutomation; showOwner?: boolean }) => {
    const template = templates.find(t => t.id === automation.templateId);
    
    return (
      <Card className="hover-elevate">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <CardTitle className="text-base flex items-center gap-2">
                {automation.name}
                {automation.scope === "STANDARD" && !isAdmin && (
                  <Lock className="w-3 h-3 text-muted-foreground" />
                )}
                <Badge variant={automation.isActive ? "default" : "secondary"}>
                  {automation.isActive ? "Active" : "Paused"}
                </Badge>
              </CardTitle>
            </div>
            {canEditAutomation(automation) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`automation-menu-${automation.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEditAutomation(automation)}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => duplicateAutomationMutation.mutate(automation)}
                    disabled={duplicateAutomationMutation.isPending}
                    data-testid={`automation-duplicate-${automation.id}`}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => {
                      setDeleteType("automation");
                      setDeleteId(automation.id);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                {automation.triggerOffsetValue === 0 
                  ? "Day of show" 
                  : `${automation.triggerOffsetValue} days ${automation.triggerType === "BEFORE_EVENT_DATE" ? "before" : "after"} show`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                Send at {automation.sendTime || "09:00"} 
                ({automation.sendTimezone === "CLIENT" ? "client's timezone" : automation.sendTimezone?.replace("America/", "")})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span>Template: {template?.name || "Unknown"}</span>
            </div>
            {automation.assignedToUserId && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>
                  From: {automation.assignedToUserId === "CLIENT_REP" 
                    ? "Client's Rep" 
                    : (() => {
                        const sender = availableSenders.find(s => s.id === automation.assignedToUserId);
                        if (sender) return `${sender.name} (${sender.email})`;
                        const userProfile = allUsers.find(u => u.userId === automation.assignedToUserId);
                        return userProfile?.gmailConnectedEmail || automation.assignedToUserId;
                      })()}
                </span>
              </div>
            )}
          </div>
          {showOwner && automation.ownerUserId && (
            <p className="text-xs text-muted-foreground mt-2">Created by: {automation.createdBy}</p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex-1 overflow-auto p-3 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-page-title">Communications</h1>
            <p className="text-muted-foreground hidden sm:block">Manage email templates, automations, and send bulk emails</p>
          </div>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={["admin-tools", "automations", "confirmations"].includes(activeTab) ? "default" : "outline"} 
                  data-testid="button-admin-tools"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Tools
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => setActiveTab("admin-tools")}
                  data-testid="menu-suppressed-leads"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suppressed Leads
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setActiveTab("automations")}
                  data-testid="menu-automations"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Automations
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setActiveTab("confirmations")}
                  data-testid="menu-confirmations"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Confirmations
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="send" data-testid="tab-send" className="flex items-center gap-1 sm:gap-2">
              <Send className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Mass Email</span>
              <span className="sm:hidden text-[10px]">Email</span>
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates" className="flex items-center gap-1 sm:gap-2">
              <FileText className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Templates</span>
              <span className="sm:hidden text-[10px]">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="drip-campaign" data-testid="tab-drip-campaign" className="flex items-center gap-1 sm:gap-2">
              <Droplets className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Drip Campaign</span>
              <span className="sm:hidden text-[10px]">Drip</span>
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="flex items-center gap-1 sm:gap-2">
              <CalendarClock className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Scheduled</span>
              <span className="sm:hidden text-[10px]">Sched</span>
            </TabsTrigger>
            <TabsTrigger value="sms" data-testid="tab-sms" className="flex items-center gap-1 sm:gap-2">
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">SMS</span>
              <span className="sm:hidden text-[10px]">SMS</span>
            </TabsTrigger>
          </TabsList>

          {/* Outbox moved to the Email Hub (/inbox?folder=outbox). Lives next
              to Inbox/Drafts/Sent so users see all mailbox folders in one
              place. EmailDraftsTab is rendered there. */}

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-templates"
                />
              </div>
              <Button
                onClick={() => {
                  resetTemplateForm();
                  setIsTemplateDialogOpen(true);
                }}
                data-testid="button-new-template"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>

            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <>
                {/* Task #696: respect "Show hidden" toggle on every section
                    of the manager. Non-admins never see hidden rows here
                    either way (they can't toggle the flag). */}
                {/* My Templates */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">My Templates</h3>
                  {(() => {
                    const myFiltered = showHiddenInManager
                      ? myTemplates
                      : myTemplates.filter((t) => !t.hiddenFromPickers);
                    const visible = filterBySearch(myFiltered);
                    if (visible.length === 0) {
                      return (
                        <Card className="p-6 text-center text-muted-foreground">
                          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>You haven't created any templates yet</p>
                          <Button
                            variant="outline"
                            className="mt-3"
                            onClick={() => {
                              resetTemplateForm();
                              setIsTemplateDialogOpen(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Template
                          </Button>
                        </Card>
                      );
                    }
                    return (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {visible.map((template) => (
                          <TemplateCard key={template.id} template={template} />
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Master Templates */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="text-lg font-semibold">Master Templates</h3>
                    {/* Admin-only visibility filter. Mirrors the "Visible to" badge:
                        non-admins already only see master templates they can use, so
                        the slicer would be redundant for them. */}
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        {/* Task #696: "Show hidden" filter for the manager.
                            Default ON so admins always start with the full
                            list — pickers themselves still hide these. */}
                        <label
                          htmlFor="filter-show-hidden"
                          className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
                        >
                          <Switch
                            id="filter-show-hidden"
                            checked={showHiddenInManager}
                            onCheckedChange={setShowHiddenInManager}
                            data-testid="switch-show-hidden"
                          />
                          Show hidden
                        </label>
                        <Label
                          htmlFor="filter-master-visibility"
                          className="text-xs text-muted-foreground"
                        >
                          Visible to
                        </Label>
                        <Select
                          value={masterVisibilityFilter}
                          onValueChange={(v) => setMasterVisibilityFilter(v as "ALL" | "EVERYONE" | UserRole)}
                        >
                          <SelectTrigger
                            id="filter-master-visibility"
                            className="h-8 w-[160px]"
                            data-testid="select-master-visibility-filter"
                          >
                            <SelectValue placeholder="All templates" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL" data-testid="option-master-visibility-all">All templates</SelectItem>
                            <SelectItem value="EVERYONE" data-testid="option-master-visibility-everyone">Everyone</SelectItem>
                            <SelectItem value="sales_rep" data-testid="option-master-visibility-sales_rep">Sales Rep</SelectItem>
                            <SelectItem value="artist" data-testid="option-master-visibility-artist">Talent</SelectItem>
                            <SelectItem value="billing" data-testid="option-master-visibility-billing">Billing</SelectItem>
                            <SelectItem value="admin" data-testid="option-master-visibility-admin">Admin</SelectItem>
                            {/* v2: <SelectItem value="marketing" data-testid="option-master-visibility-marketing">Marketing</SelectItem> */}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  {(() => {
                    // Apply the admin visibility filter before search-narrowing so
                    // the search count reflects the slice the admin selected.
                    const filteredByVisibility = isAdmin
                      ? standardTemplates.filter((t) => {
                          const roles = (t.visibleToRoles as string[] | null) || [];
                          if (masterVisibilityFilter === "ALL") return true;
                          if (masterVisibilityFilter === "EVERYONE") return roles.length === 0;
                          return roles.includes(masterVisibilityFilter);
                        })
                      : standardTemplates;
                    // Task #696: respect the admin's "Show hidden" toggle.
                    // Non-admins never see hidden master templates either way.
                    const filteredByHidden = isAdmin
                      ? (showHiddenInManager
                          ? filteredByVisibility
                          : filteredByVisibility.filter((t) => !t.hiddenFromPickers))
                      : filteredByVisibility.filter((t) => !t.hiddenFromPickers);
                    const visibleTemplates = filterBySearch(filteredByHidden);
                    if (visibleTemplates.length === 0) {
                      return (
                        <Card className="p-6 text-center text-muted-foreground" data-testid="empty-master-templates">
                          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No master templates available</p>
                        </Card>
                      );
                    }
                    return (
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {visibleTemplates.map((template) => (
                          <TemplateCard key={template.id} template={template} />
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Admin: All User Templates */}
                {isAdmin && allUserTemplates.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">All User Templates</h3>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {/* Task #696: respect "Show hidden" toggle. */}
                      {filterBySearch(
                        showHiddenInManager
                          ? allUserTemplates
                          : allUserTemplates.filter((t) => !t.hiddenFromPickers)
                      ).map((template) => (
                        <TemplateCard key={template.id} template={template} showOwner />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Drip Campaign Tab */}
          <TabsContent value="scheduled" className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Scheduled emails</h2>
              <p className="text-sm text-muted-foreground">
                Pending sends queued from any composer (mass email, invoice, payment confirmation,
                booking confirmation). Cancel or reschedule any pending row.
              </p>
            </div>
            <ScheduledEmailsList />
          </TabsContent>

          <TabsContent value="drip-campaign" className="space-y-6">
            <DripCampaignTab />
          </TabsContent>

          {/* SMS Tab */}
          <TabsContent value="sms" className="space-y-6">
            <SmsCampaignTab />
          </TabsContent>

          {/* Automations Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="automations" className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search automations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="input-search-automations"
                  />
                </div>
                <Button
                  onClick={() => {
                    resetAutomationForm();
                    setIsAutomationDialogOpen(true);
                  }}
                  data-testid="button-new-automation"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Automation
                </Button>
              </div>

              {automationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : filterBySearch(automations).length === 0 ? (
                <Card className="p-6 text-center text-muted-foreground">
                  <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No automations created yet</p>
                  <p className="text-sm mt-1">Create automations to send emails before or after show dates</p>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filterBySearch(automations).map(automation => (
                    <AutomationCard key={automation.id} automation={automation} showOwner />
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Confirmation Emails Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="confirmations" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Confirmation Email Templates</CardTitle>
                  <CardDescription>
                    Customize booking confirmation emails for each client type. These templates are sent automatically when a booking is confirmed.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Client Type</Label>
                      <Select value={confirmationClientType} onValueChange={setConfirmationClientType}>
                        <SelectTrigger data-testid="select-confirmation-client-type">
                          <SelectValue placeholder="Select client type" />
                        </SelectTrigger>
                        <SelectContent>
                          {clientTypeOptions.map((ct) => (
                            <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Talent</Label>
                      <Select value={confirmationTalentId} onValueChange={setConfirmationTalentId}>
                        <SelectTrigger data-testid="select-confirmation-talent">
                          <SelectValue placeholder="Select talent">
                            {talent.find(e => e.id === confirmationTalentId)?.name || "Select talent"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {activeTalent.map((talent) => (
                            <SelectItem key={talent.id} value={talent.id} data-testid={`option-talent-${talent.id}`}>
                              {talent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Event Offering</Label>
                      <Select value={confirmationEventOfferingId} onValueChange={setConfirmationEventOfferingId}>
                        <SelectTrigger data-testid="select-confirmation-event-offering">
                          <SelectValue placeholder="Select show offering">
                            {eventOfferings.find(o => o.id === confirmationEventOfferingId)?.name || "Select show offering"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {activeEventOfferings.map((offering) => (
                            <SelectItem key={offering.id} value={offering.id} data-testid={`option-event-offering-${offering.id}`}>
                              {offering.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmation-subject">Email Subject</Label>
                    <VoiceToTextInput
                      id="confirmation-subject"
                      placeholder="Subject line..."
                      value={confirmationSubject}
                      onChange={(e) => setConfirmationSubject(e.target.value)}
                      data-testid="input-confirmation-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="confirmation-body">Email Body</Label>
                      <VoiceToTextButton onTranscript={(t) => confirmationBodyEditorRef.current?.insertAtCursor(t)} />
                    </div>
                    <RichTextEditor
                      ref={confirmationBodyEditorRef}
                      value={confirmationBody}
                      onChange={setConfirmationBody}
                      placeholder="Email content..."
                      minHeight="300px"
                    />
                  </div>

                  <div className="rounded-lg bg-muted p-4 text-sm">
                    <h4 className="font-medium mb-2">Available Variables</h4>
                    <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                      <div><code className="text-xs bg-background px-1 rounded">{"{{client_name}}"}</code> - Client/Organization name</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{contact_name}}"}</code> - Contact person name</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{date}}"}</code> - Event date</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{event_time}}"}</code> - Event time</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{location}}"}</code> - Venue address</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{booking_details}}"}</code> - Event details / performance summary</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{invoice_number}}"}</code> - Invoice number</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{invoice_url}}"}</code> - Link to invoice</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{amount}}"}</code> - Total price (rate - discount)</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{booking_total}}"}</code> - Base rate amount</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{talent}}"}</code> - Talent name</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{event_offering}}"}</code> - Event offering / service</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{sender_name}}"}</code> - Sender name</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      {selectedConfirmationTemplate ? (
                        <span>Last updated: {new Date(selectedConfirmationTemplate.updatedAt!).toLocaleDateString()}</span>
                      ) : (
                        <span>No template saved for this client type yet</span>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        saveConfirmationTemplateMutation.mutate({
                          clientType: confirmationClientType,
                          talentId: confirmationTalentId || null,
                          eventOfferingId: confirmationEventOfferingId || null,
                          subject: confirmationSubject,
                          body: confirmationBody,
                          isActive: true,
                        });
                      }}
                      disabled={saveConfirmationTemplateMutation.isPending || !confirmationSubject || !confirmationBody}
                      data-testid="button-save-confirmation-template"
                    >
                      {saveConfirmationTemplateMutation.isPending && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Save Template
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Standalone system template — Booked Without Calendar */}
              <Card className="border-blue-200 dark:border-blue-800" data-testid="card-no-calendar-template">
                <CardHeader>
                  <CardTitle>Booked Without Calendar</CardTitle>
                  <CardDescription>
                    Sent to a talent when they're assigned to a booking but haven't connected their Google Calendar.
                    Includes a personalized connect-calendar link that expires in 7 days.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="no-calendar-subject">Email Subject</Label>
                    <VoiceToTextInput
                      id="no-calendar-subject"
                      placeholder="Subject line..."
                      value={noCalSubject}
                      onChange={(e) => setNoCalSubject(e.target.value)}
                      data-testid="input-no-calendar-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="no-calendar-body">Email Body</Label>
                      <VoiceToTextButton onTranscript={(t) => noCalBodyEditorRef.current?.insertAtCursor(t)} />
                    </div>
                    <RichTextEditor
                      ref={noCalBodyEditorRef}
                      value={noCalBody}
                      onChange={setNoCalBody}
                      placeholder="Email content..."
                      minHeight="300px"
                    />
                  </div>

                  <div className="rounded-lg bg-muted p-4 text-sm">
                    <h4 className="font-medium mb-2">Available Variables</h4>
                    <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                      <div><code className="text-xs bg-background px-1 rounded">{"{{talent_name}}"}</code> - Talent's name</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{account_name}}"}</code> - Client / venue name</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{event_date}}"}</code> - Formatted event date</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{event_time}}"}</code> - Event time</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{event_details}}"}</code> - Event description</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{venue_address}}"}</code> - Venue address</div>
                      <div><code className="text-xs bg-background px-1 rounded">{"{{connect_calendar_url}}"}</code> - Personalized connect link</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      {confirmationTemplates.find(t => t.clientType === NO_CALENDAR_TEMPLATE_CLIENT_TYPE) ? (
                        <span>Last updated: {new Date(confirmationTemplates.find(t => t.clientType === NO_CALENDAR_TEMPLATE_CLIENT_TYPE)!.updatedAt!).toLocaleDateString()}</span>
                      ) : (
                        <span>Using default copy — not yet customized</span>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        saveConfirmationTemplateMutation.mutate({
                          clientType: NO_CALENDAR_TEMPLATE_CLIENT_TYPE,
                          talentId: null,
                          eventOfferingId: null,
                          subject: noCalSubject,
                          body: noCalBody,
                          isActive: true,
                        });
                      }}
                      disabled={saveConfirmationTemplateMutation.isPending || !noCalSubject || !noCalBody}
                      data-testid="button-save-no-calendar-template"
                    >
                      {saveConfirmationTemplateMutation.isPending && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      Save Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Admin Tools Tab */}
          {isAdmin && (
            <TabsContent value="admin-tools" className="space-y-6">
              {/* Email Health and Mass Email Settings have moved to Admin Panel → Email Settings */}
              <Card data-testid="card-email-health-moved">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Health & Mass Email Settings have moved
                  </CardTitle>
                  <CardDescription>
                    Sender reputation, domain authentication, mass email compliance, and alerts now live in the Admin Panel under "Email Settings". Suppressed Leads remain here for quick triage.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Suppressed Leads */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ban className="w-5 h-5" />
                    Suppressed Leads
                  </CardTitle>
                  <CardDescription>
                    Leads that have been suppressed due to opt-out replies or manual action
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {suppressedLeadsQuery.isLoading ? (
                    <p className="text-muted-foreground">Loading suppressed leads...</p>
                  ) : suppressedLeads.length === 0 ? (
                    <p className="text-muted-foreground">No suppressed leads.</p>
                  ) : (
                    <div className="space-y-2">
                      {suppressedLeads.map((lead) => (
                        <div key={lead.id} className="flex items-center justify-between p-3 border rounded-md">
                          <div>
                            <p className="font-medium">{lead.leadName || lead.contactName || "Unnamed Lead"}</p>
                            <p className="text-sm text-muted-foreground">{lead.emailAddress}</p>
                            <p className="text-xs text-muted-foreground">
                              Suppressed: {lead.suppressedReason} 
                              {lead.suppressedAt && ` on ${new Date(lead.suppressedAt).toLocaleDateString()}`}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnsuppressLead(lead.id)}
                            data-testid={`button-unsuppress-${lead.id}`}
                          >
                            Unsuppress
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Send Tab */}
          <TabsContent value="send" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Mass Email</CardTitle>
                <CardDescription>Send emails to multiple recipients at once</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Recipients */}
                <div className="space-y-3">
                  <Label>Recipients</Label>
                  <div className="flex gap-3">
                    {/* Region selector */}
                    <div className="flex-1">
                      <Popover open={regionPopoverOpen} onOpenChange={setRegionPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={regionPopoverOpen}
                            className="w-full justify-between"
                            data-testid="select-region"
                          >
                            {sendRegion
                              ? regions.find(r => r.id === sendRegion)?.name
                              : "Select a region..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search regions..." data-testid="input-search-region" />
                            <CommandList>
                              <CommandEmpty>No region found.</CommandEmpty>
                              <CommandGroup>
                                {regions.map(region => (
                                  <CommandItem
                                    key={region.id}
                                    value={region.name}
                                    onSelect={() => {
                                      setSendRegion(region.id);
                                      setRegionPopoverOpen(false);
                                    }}
                                    data-testid={`region-option-${region.id}`}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        sendRegion === region.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {region.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Audience multi-select */}
                    <div className="flex-1">
                      <Popover open={audiencePopoverOpen} onOpenChange={setAudiencePopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={audiencePopoverOpen}
                            className="w-full justify-between"
                            data-testid="select-audience"
                          >
                            {sendAudience.length > 0
                              ? sendAudience.length === 1
                                ? audienceOptions.find(o => o.value === sendAudience[0])?.label
                                : `${sendAudience.length} audiences selected`
                              : "Select audience..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search audience..." data-testid="input-search-audience" />
                            <CommandList>
                              <CommandEmpty>No audience found.</CommandEmpty>
                              <CommandGroup>
                                {audienceOptions.map(option => (
                                  <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => {
                                      setSendAudience(prev => 
                                        prev.includes(option.value)
                                          ? prev.filter(v => v !== option.value)
                                          : [...prev, option.value]
                                      );
                                    }}
                                    data-testid={`audience-option-${option.value}`}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        sendAudience.includes(option.value) ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {option.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {/* Task #685 — Cold-tier sending domain badge. Hidden from
                    reps per the "auto-rotate invisibly" cold-email vision —
                    reps just hit Send; the admin policy + cold-domain
                    plumbing decide routing. Admins still see the badge so
                    they can spot a misconfiguration. Reps will only see a
                    pre-flight error if no verified cold domain is found. */}
                {willUseColdTier && isAdmin && (
                  <div data-testid="cold-tier-domain-badge" className="flex items-start gap-2">
                    {!coldDomainQueryReady && !coldDomainQueryFailed ? (
                      <Badge
                        variant="outline"
                        className="text-xs font-normal"
                        data-testid="badge-cold-domain-loading"
                      >
                        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                        Cold tier — checking sending domain…
                      </Badge>
                    ) : coldDomainQueryFailed ? (
                      <Badge
                        variant="outline"
                        className="text-xs font-normal"
                        data-testid="badge-cold-domain-error"
                      >
                        <AlertCircle className="w-3 h-3 mr-1.5" />
                        Cold tier — couldn't check sending domain. Try again in a moment.
                      </Badge>
                    ) : coldDomainVerified ? (
                      <Badge
                        variant="secondary"
                        className="text-xs font-normal"
                        data-testid="badge-cold-domain-verified"
                      >
                        <ShieldCheck className="w-3 h-3 mr-1.5" />
                        Cold tier — sending from{" "}
                        <code className="ml-1 font-mono" data-testid="text-cold-domain">
                          {coldSendingDomain!.domain}
                        </code>
                        {coldSendingDomain!.fromAddress && (
                          <span className="ml-1 text-muted-foreground">
                            (as <code className="font-mono">{coldSendingDomain!.fromAddress}</code>)
                          </span>
                        )}
                      </Badge>
                    ) : (
                      <Badge
                        variant="destructive"
                        className="text-xs font-normal"
                        data-testid="badge-cold-domain-missing"
                      >
                        <AlertCircle className="w-3 h-3 mr-1.5" />
                        Cold tier — no verified sending domain.{" "}
                        <Link
                          href="/admin?section=emailSettings&tab=cold&byod=1"
                          className="ml-1 underline"
                          data-testid="link-cold-domain-setup"
                        >
                          Set one up in Email settings
                        </Link>
                      </Badge>
                    )}
                  </div>
                )}

                {/* Cold / Warm sending mode */}
                {coldEmailAllowed && (
                  <ColdWarmToggle
                    value={sendRiskTier}
                    onChange={(v) => {
                      setSendRiskTierTouched(true);
                      setSendRiskTier(v);
                    }}
                    warmLocked={overrideLocked && willUseColdTier}
                    isLeadAudience={willUseColdTier}
                  />
                )}

                {/* Template Selection */}
                <div className="space-y-2">
                  <Label>Use Template (Optional)</Label>
                  <Select value={sendSelectedTemplate} onValueChange={handleTemplateSelect}>
                    <SelectTrigger data-testid="select-template">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Task #696: filter out picker-hidden templates from
                          human-facing send picker. Manager UI below still shows
                          them so admins can flip the toggle back. */}
                      {myTemplates.filter(t => !t.hiddenFromPickers).length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">My Templates</div>
                          {myTemplates.filter(t => !t.hiddenFromPickers).map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </>
                      )}
                      {standardTemplates.filter(t => !t.hiddenFromPickers).length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Master Templates</div>
                          {standardTemplates.filter(t => !t.hiddenFromPickers).map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="sendSubject">Subject</Label>
                  <VoiceToTextInput
                    id="sendSubject"
                    value={sendSubject}
                    onChange={(e) => setSendSubject(e.target.value)}
                    placeholder="Email subject..."
                    data-testid="input-send-subject"
                  />
                </div>

                {/* CC */}
                <CcEmailInput value={sendCc} onChange={setSendCc} data-testid="input-bulk-cc" />

                {/* Body */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="sendBody">Message</Label>
                    <VoiceToTextButton onTranscript={(t) => sendBodyEditorRef.current?.insertAtCursor(t)} />
                  </div>
                  <RichTextEditor
                    ref={sendBodyEditorRef}
                    value={sendBody}
                    onChange={setSendBody}
                    placeholder="Write your message..."
                    minHeight="200px"
                    showMergeTokens={true}
                    mergeTokens={[
                      { token: "{{contact_name}}", label: "Contact Name" },
                      { token: "{{account_name}}", label: "Account Name" },
                      { token: "{{sender_name}}", label: "Sender Name" },
                    ]}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use the <span className="font-medium">Variables</span> button in the toolbar to insert merge fields that personalize each email automatically.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleSendBulkEmail}
                    disabled={!sendSubject || !sendBody || sendBulkEmailMutation.isPending || isLoadingCapacity || sendAudience.length === 0}
                    data-testid="button-send-bulk"
                  >
                    {(sendBulkEmailMutation.isPending || isLoadingCapacity) ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {isLoadingCapacity ? "Calculating..." : "Send Mass Email"}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsScheduleDialogOpen(true)}
                    disabled={!sendSubject || !sendBody || isLoadingCapacity || sendAudience.length === 0}
                    data-testid="button-schedule-send"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Schedule Send
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Campaign Activity Section */}
            <Card data-testid="card-campaign-activity">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Campaign Activity
                    </CardTitle>
                    <CardDescription>Recent mass email campaigns and their status</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refetchCampaigns()}
                    data-testid="button-refresh-campaigns"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No campaigns sent yet</p>
                    <p className="text-xs mt-1">Your mass email campaigns will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {campaigns.slice(0, 10).map((campaign) => {
                      const statusColor = 
                        campaign.status === "COMPLETED" ? "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" :
                        campaign.status === "SENDING" ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" :
                        campaign.status === "PAUSED" ? "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20" :
                        campaign.status === "DRAFT" ? "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20" :
                        "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20";
                      
                      const sentDate = campaign.sentAt ? new Date(campaign.sentAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      }) : campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      }) : '-';

                      return (
                        <div 
                          key={campaign.id}
                          className="flex items-center justify-between p-3 rounded-lg border hover-elevate cursor-pointer"
                          onClick={() => {
                            setSelectedCampaignId(campaign.id);
                            setIsCampaignDetailOpen(true);
                          }}
                          data-testid={`campaign-row-${campaign.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate" data-testid={`campaign-subject-${campaign.id}`}>
                              {campaign.subject || campaign.name || "Untitled Campaign"}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid={`campaign-date-${campaign.id}`}>
                              {sentDate}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 ml-4">
                            <div className="text-right">
                              <p className="text-sm font-medium" data-testid={`campaign-sent-count-${campaign.id}`}>
                                {campaign.sentCount}/{campaign.totalRecipients}
                              </p>
                              <p className="text-xs text-muted-foreground">emails sent</p>
                            </div>
                            <Badge className={statusColor} data-testid={`campaign-status-${campaign.id}`}>
                              {campaign.status}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                    {campaigns.length > 10 && (
                      <p className="text-xs text-center text-muted-foreground pt-2">
                        Showing 10 of {campaigns.length} campaigns
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Template Dialog */}
      <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
        <DialogContent className="w-full sm:max-w-[700px] max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>{editingTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
            <DialogDescription>
              {editingTemplate ? "Update your email template" : "Create a new email template for reuse"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {isAdmin && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 mb-4" data-testid="admin-template-scope-ribbon">
                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Template Type:</span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={templateScope === "STANDARD" ? "default" : "outline"}
                    onClick={() => setTemplateScope("STANDARD")}
                    className={templateScope === "STANDARD" ? "bg-amber-600 hover:bg-amber-700" : ""}
                    data-testid="button-scope-master"
                  >
                    Master Template
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={templateScope === "USER" ? "default" : "outline"}
                    onClick={() => setTemplateScope("USER")}
                    className={templateScope === "USER" ? "bg-amber-600 hover:bg-amber-700" : ""}
                    data-testid="button-scope-personal"
                  >
                    My Template
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="templateName">Template Name</Label>
                <Input
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Follow-up Email"
                  data-testid="input-template-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="templateSubject">Subject</Label>
                <VoiceToTextInput
                  id="templateSubject"
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="Email subject line..."
                  data-testid="input-template-subject"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="templateBody">Body</Label>
                  <div className="flex items-center gap-2">
                    <VoiceToTextButton onTranscript={(t) => templateBodyEditorRef.current?.insertAtCursor(t)} />
                    <AIDraftButton
                      kind="standard_email"
                      context={`Subject: ${templateSubject || "(none)"}\nCurrent body:\n${templateBody || "(empty)"}`}
                      onDraft={(text) => setTemplateBody(text)}
                    />
                  </div>
                </div>
                <RichTextEditor
                  ref={templateBodyEditorRef}
                  value={templateBody}
                  onChange={setTemplateBody}
                  placeholder="Email content..."
                  minHeight="180px"
                />
                <p className="text-xs text-muted-foreground">
                  Available variables: {"{{contact_name}}"}, {"{{account_name}}"}, {"{{date}}"}, {"{{event_time}}"}, {"{{location}}"}, {"{{fee}}"}, {"{{invoice_number}}"}, {"{{invoice_url}}"}, {"{{talent}}"}, {"{{sender_name}}"}, {"{{media_assets_url}}"}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="templateTags">Tags (comma-separated)</Label>
                <Input
                  id="templateTags"
                  value={templateTags}
                  onChange={(e) => setTemplateTags(e.target.value)}
                  placeholder="e.g., confirmation, follow-up"
                  data-testid="input-template-tags"
                />
              </div>
              {/* Task #696: admin-only "Hide from template pickers" toggle.
                  Available on every scope so admins can hide either master or
                  user-scope automation-only templates. Hidden rows still resolve
                  by id / seedKey / name from automations and schedulers. */}
                {isAdmin && (
                  <div className="space-y-2" data-testid="template-hidden-section">
                    <label
                      className="flex items-start gap-2 p-2 rounded-md border hover-elevate cursor-pointer"
                    >
                      <Checkbox
                        checked={templateHiddenFromPickers}
                        onCheckedChange={(value) => setTemplateHiddenFromPickers(!!value)}
                        data-testid="checkbox-hidden-from-pickers"
                      />
                      <div className="space-y-0.5">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <EyeOff className="w-3.5 h-3.5" />
                          Hide from template pickers
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Removes this template from the composer, inbox replies,
                          invoice email, mass email, and other interactive pickers.
                          Automations and schedulers that resolve by id, seedKey, or
                          canonical name still use it.
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              {/* Visible-to role checkboxes are admin-only and only shown for
                  STANDARD (master) templates. Empty list = visible to all. */}
              {isAdmin && templateScope === "STANDARD" && (
                <div className="space-y-2" data-testid="template-visibility-section">
                  <Label>Visible to</Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which roles see this master template. Leave all unchecked to show it to everyone. Admins always see every master template.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {VISIBILITY_ROLE_OPTIONS.map(opt => {
                      const checked = templateVisibleToRoles.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 p-2 rounded-md border hover-elevate cursor-pointer"
                          data-testid={`visibility-role-${opt.value}`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              setTemplateVisibleToRoles(prev =>
                                value
                                  ? Array.from(new Set([...prev, opt.value]))
                                  : prev.filter(r => r !== opt.value)
                              );
                            }}
                            data-testid={`checkbox-visibility-${opt.value}`}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={!templateName || !templateSubject || !templateBody || createTemplateMutation.isPending || updateTemplateMutation.isPending}
              data-testid="button-save-template"
            >
              {(createTemplateMutation.isPending || updateTemplateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingTemplate ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Automation Dialog */}
      <Dialog open={isAutomationDialogOpen} onOpenChange={setIsAutomationDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingAutomation ? "Edit Automation" : "Create Automation"}</DialogTitle>
            <DialogDescription>
              {editingAutomation ? "Update your email automation" : "Set up automated emails to send before or after show dates"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="automationName">Automation Name</Label>
              <Input
                id="automationName"
                value={automationName}
                onChange={(e) => setAutomationName(e.target.value)}
                placeholder="e.g., Day-of-Show Reminder"
                data-testid="input-automation-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trigger</Label>
                <Select value={automationTriggerType} onValueChange={setAutomationTriggerType}>
                  <SelectTrigger data-testid="select-trigger-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BEFORE_EVENT_DATE">Before Event Date</SelectItem>
                    <SelectItem value="AFTER_EVENT_DATE">After Event Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Days {automationTriggerType === "BEFORE_EVENT_DATE" ? "Before" : "After"}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={automationTriggerOffset}
                    onChange={(e) => setAutomationTriggerOffset(e.target.value)}
                    className="w-24"
                    min="0"
                    data-testid="input-trigger-offset"
                  />
                  <span className="text-sm text-muted-foreground">
                    {automationTriggerOffset === "0" ? "(day of event)" : "days"}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Send Time</Label>
                <Input
                  type="time"
                  value={automationSendTime}
                  onChange={(e) => setAutomationSendTime(e.target.value)}
                  data-testid="input-send-time"
                />
              </div>

              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select value={automationSendTimezone} onValueChange={setAutomationSendTimezone}>
                  <SelectTrigger data-testid="select-send-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Client's Address Timezone</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                    <SelectItem value="America/Phoenix">Arizona (MST)</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii Time (HST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Template</Label>
              <Select value={automationTemplateId} onValueChange={setAutomationTemplateId}>
                <SelectTrigger data-testid="select-automation-template">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Send Email From</Label>
              <Select value={automationAssignedToUserId} onValueChange={setAutomationAssignedToUserId}>
                <SelectTrigger data-testid="select-assign-user">
                  <SelectValue placeholder="Select sender">
                    {automationAssignedToUserId === "CLIENT_REP" 
                      ? "Client's Assigned Rep"
                      : automationAssignedToUserId === "connector"
                        ? (() => {
                            const connector = availableSenders.find(s => s.type === 'connector');
                            return connector ? `${connector.name} (${connector.email})` : "Workspace Email";
                          })()
                        : automationAssignedToUserId && automationAssignedToUserId !== "none"
                          ? (() => {
                              const sender = availableSenders.find(s => s.id === automationAssignedToUserId);
                              if (sender) return `${sender.name} (${sender.email})`;
                              const user = allUsers.find(u => u.id === automationAssignedToUserId);
                              return user ? `${user.userId} ${user.gmailConnectedEmail ? `(${user.gmailConnectedEmail})` : ""}`.trim() : "Select sender";
                            })()
                          : "Select sender"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignment</SelectItem>
                  <SelectItem value="CLIENT_REP">Client's Assigned Rep</SelectItem>
                  {/* Workspace-connected emails */}
                  {availableSenders.filter(s => s.type === 'connector').map(sender => (
                    <SelectItem key={sender.id} value={sender.id} data-testid={`option-sender-${sender.id}`}>
                      {sender.name} ({sender.email})
                    </SelectItem>
                  ))}
                  {/* User-connected emails */}
                  {availableSenders.filter(s => s.type === 'user').map(sender => (
                    <SelectItem key={sender.id} value={sender.id} data-testid={`option-sender-${sender.id}`}>
                      {sender.name} ({sender.email})
                    </SelectItem>
                  ))}
                  {/* Fallback: Users without connected emails from availableSenders */}
                  {allUsers.filter(u => !availableSenders.some(s => s.id === u.id)).map(u => (
                    <SelectItem key={u.id} value={u.id} data-testid={`option-assign-user-${u.id}`}>
                      {u.name || u.email || u.id} {u.gmailConnectedEmail ? `(${u.gmailConnectedEmail})` : "(no email connected)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose who the email will be sent from. Select a workspace email or "Client's Assigned Rep" to use whoever is assigned to the client.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Limit to Client Types</Label>
              <div className="border rounded-md p-2 space-y-1 max-h-40 overflow-auto">
                {clientTypesData.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1 py-1">No client types configured.</p>
                )}
                {clientTypesData.map((ct) => {
                  const checked = automationClientTypeIds.includes(ct.id);
                  return (
                    <label
                      key={ct.id}
                      className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-muted cursor-pointer"
                      data-testid={`row-automation-clienttype-${ct.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setAutomationClientTypeIds((prev) =>
                            prev.includes(ct.id) ? prev.filter((x) => x !== ct.id) : [...prev, ct.id]
                          );
                        }}
                        data-testid={`checkbox-automation-clienttype-${ct.id}`}
                      />
                      {ct.name}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to send to all client types. Otherwise, only clients with a matching client type will be included.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="automationRequireUnpaid"
                checked={automationRequireUnpaidInvoice}
                onChange={(e) => setAutomationRequireUnpaidInvoice(e.target.checked)}
                className="rounded"
                data-testid="checkbox-require-unpaid"
              />
              <Label htmlFor="automationRequireUnpaid">Only if invoice is not marked paid</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="automationActive"
                checked={automationIsActive}
                onChange={(e) => setAutomationIsActive(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="automationActive">Active</Label>
            </div>

            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                Emails will be sent automatically to the client associated with each booking when the trigger conditions are met.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAutomationDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAutomation}
              disabled={!automationName || !automationTemplateId || createAutomationMutation.isPending || updateAutomationMutation.isPending}
              data-testid="button-save-automation"
            >
              {(createAutomationMutation.isPending || updateAutomationMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingAutomation ? "Update Automation" : "Create Automation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteType === "template" ? "Template" : "Automation"}?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the {deleteType}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteTemplateMutation.isPending || deleteAutomationMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {(deleteTemplateMutation.isPending || deleteAutomationMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Send Confirmation Dialog */}
      <Dialog open={campaignConfirmOpen} onOpenChange={setCampaignConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="dialog-campaign-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Campaign will send gradually
            </DialogTitle>
            <DialogDescription>
              This protects deliverability by avoiding sudden spikes.
            </DialogDescription>
          </DialogHeader>
          
          {campaignCapacity && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                  <p className="text-sm text-muted-foreground">Will send today</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="text-will-send-today">
                    {campaignCapacity.willSendToday}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-muted-foreground">Queued for next business day</p>
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="text-queued-later">
                    {campaignCapacity.queuedForLater}
                  </p>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-muted">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>
                    Sending window: <strong>{campaignCapacity.sendWindowStart}–{campaignCapacity.sendWindowEnd}</strong> ({campaignCapacity.timezone})
                  </span>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Total recipients: <strong>{campaignCapacity.totalRecipients}</strong></p>
                <p>Already sent today: {campaignCapacity.campaignSentToday} / {campaignCapacity.totalAllowed} (daily cap + buffer)</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setCampaignConfirmOpen(false)}
              data-testid="button-campaign-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCampaignSend}
              disabled={sendBulkEmailMutation.isPending || !campaignCapacity}
              data-testid="button-campaign-confirm"
            >
              {sendBulkEmailMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Confirm & Queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Send Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Schedule Mass Email</DialogTitle>
            <DialogDescription>
              Choose when to send this email campaign
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="schedule-date">Date</Label>
              <Input
                id="schedule-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="input-schedule-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-time">Time ({getTimeZoneAbbreviation(scheduledDate && scheduledTime ? `${scheduledDate}T${scheduledTime}` : new Date())})</Label>
              <Input
                id="schedule-time"
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                data-testid="input-schedule-time"
              />
            </div>
            
            {scheduledDate && scheduledTime && (
              <div className="p-3 rounded-lg bg-muted text-sm">
                <span className="text-muted-foreground">Email will be sent on:</span>
                <p className="font-medium mt-1">
                  {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZone: getWorkspaceTimeZone()
                  })} {getTimeZoneAbbreviation(`${scheduledDate}T${scheduledTime}`)}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsScheduleDialogOpen(false);
                setScheduledDate("");
                setScheduledTime("");
              }}
              data-testid="button-schedule-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => submitScheduleMass(false)}
              disabled={!scheduledDate || !scheduledTime || !sendSubject || !sendBody || sendAudience.length === 0 || schedulingMass}
              data-testid="button-schedule-confirm"
            >
              <Clock className="w-4 h-4 mr-2" />
              Schedule Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Dialog */}
      <Dialog open={isCampaignDetailOpen} onOpenChange={(open) => {
        setIsCampaignDetailOpen(open);
        if (!open) setSelectedCampaignId(null);
      }}>
        <DialogContent className="w-full sm:max-w-[700px] max-h-[85vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Campaign Details</DialogTitle>
            <DialogDescription>
              {selectedCampaignData?.subject || "View campaign message and recipients"}
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingCampaignDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : selectedCampaignData ? (
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {/* Campaign Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-muted text-center">
                  <p className="text-2xl font-bold">{selectedCampaignData.totalRecipients}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10 text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedCampaignData.sentCount}</p>
                  <p className="text-xs text-muted-foreground">Sent</p>
                </div>
                <div className="p-3 rounded-lg bg-red-500/10 text-center">
                  <p className="text-2xl font-bold text-red-600">{selectedCampaignData.failedCount}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-500/10 text-center">
                  <p className="text-2xl font-bold text-yellow-600">{selectedCampaignData.skippedCount + (selectedCampaignData.suppressedCount || 0)}</p>
                  <p className="text-xs text-muted-foreground">Skipped</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-500/10 text-center">
                  <p className="text-2xl font-bold text-blue-600">{(selectedCampaignData as any).openCount ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Opened</p>
                  {selectedCampaignData.sentCount > 0 && (
                    <p className="text-[10px] text-muted-foreground">{Math.round(((selectedCampaignData as any).openCount ?? 0) / selectedCampaignData.sentCount * 100)}%</p>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-purple-500/10 text-center">
                  <p className="text-2xl font-bold text-purple-600">{(selectedCampaignData as any).clickCount ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Clicked</p>
                  {selectedCampaignData.sentCount > 0 && (
                    <p className="text-[10px] text-muted-foreground">{Math.round(((selectedCampaignData as any).clickCount ?? 0) / selectedCampaignData.sentCount * 100)}%</p>
                  )}
                </div>
              </div>

              {/* Campaign Info */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge className={
                    selectedCampaignData.status === "COMPLETED" ? "bg-green-500/10 text-green-600 border-green-500/20" :
                    selectedCampaignData.status === "SENDING" ? "bg-blue-500/10 text-blue-600 border-blue-500/20" :
                    "bg-gray-500/10 text-gray-600 border-gray-500/20"
                  }>
                    {selectedCampaignData.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Sent At</span>
                  <span className="text-sm">
                    {selectedCampaignData.sentAt 
                      ? new Date(selectedCampaignData.sentAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })
                      : '-'
                    }
                  </span>
                </div>
              </div>

              {/* Failed-recipients banner with Edit & Retry. Only renders when
                  the scheduler reported any failed rows on this campaign. */}
              {selectedCampaignData.failedCount > 0 && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-2" data-testid="banner-campaign-failed">
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1 min-w-0">
                      <p className="text-sm font-medium text-red-600 dark:text-red-400">
                        {selectedCampaignData.failedCount} recipient{selectedCampaignData.failedCount === 1 ? "" : "s"} failed to send.
                      </p>
                      {(() => {
                        const sample = selectedCampaignData.recipients?.find(
                          (r: any) => r.status === "FAILED" && r.failedReason,
                        );
                        return sample ? (
                          <p className="text-xs text-muted-foreground break-words" data-testid="text-campaign-failure-reason">
                            {sample.failedReason}
                          </p>
                        ) : null;
                      })()}
                    </div>
                    {!isEditingRetry && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingRetry(true);
                          setRetryGuardError(null);
                          setRetryEditSubject(selectedCampaignData.subject || "");
                          setRetryEditBody(selectedCampaignData.body || "");
                        }}
                        data-testid="button-edit-retry-campaign"
                      >
                        Edit & retry failed
                      </Button>
                    )}
                  </div>
                  {retryGuardError && retryGuardError.length > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400" data-testid="text-retry-guard-tokens">
                      Still leaking: {retryGuardError.join(", ")}
                    </p>
                  )}
                </div>
              )}

              {/* Subject — editable when retrying. */}
              <div className="space-y-1">
                <Label>Subject</Label>
                {isEditingRetry ? (
                  <VoiceToTextInput
                    value={retryEditSubject}
                    onChange={(e) => setRetryEditSubject(e.target.value)}
                    data-testid="input-retry-subject"
                  />
                ) : (
                  <div className="p-3 rounded-lg bg-muted">
                    <p className="font-medium">{selectedCampaignData.subject}</p>
                  </div>
                )}
              </div>

              {/* Message Body — editable when retrying. */}
              <div className="space-y-1">
                <Label>Message</Label>
                {isEditingRetry ? (
                  <VoiceToTextTextarea
                    value={retryEditBody}
                    onChange={(e) => setRetryEditBody(e.target.value)}
                    rows={10}
                    className="font-mono text-xs"
                    data-testid="textarea-retry-body"
                  />
                ) : (
                  <div
                    className="p-4 rounded-lg border bg-background prose prose-sm max-w-none dark:prose-invert"
                    // Campaign bodies can carry rich HTML composed in the
                    // editor — sanitize through the shared allow-list so
                    // the strict CSP isn't undermined by injected scripts
                    // / on*= handlers / javascript: URLs.
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(selectedCampaignData.body || '') }}
                  />
                )}
              </div>

              {isEditingRetry && (
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditingRetry(false);
                      setRetryGuardError(null);
                    }}
                    data-testid="button-cancel-retry"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={retryFailedCampaignMutation.isPending}
                    onClick={() => {
                      if (!selectedCampaignData) return;
                      retryFailedCampaignMutation.mutate({
                        id: selectedCampaignData.id,
                        subject: retryEditSubject,
                        body: retryEditBody,
                      });
                    }}
                    data-testid="button-save-retry"
                  >
                    {retryFailedCampaignMutation.isPending && (
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    )}
                    Save & retry failed
                  </Button>
                </div>
              )}

              {/* Recipients with tracking */}
              {selectedCampaignData.recipients && selectedCampaignData.recipients.length > 0 && (
                <div className="space-y-1">
                  <Label>Recipients ({selectedCampaignData.recipients.length})</Label>
                  <div className="rounded-lg border divide-y max-h-64 overflow-y-auto">
                    {selectedCampaignData.recipients.map((r: any) => (
                      <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{r.recipientName || r.email}</p>
                          <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 shrink-0">
                          {r.status === 'SENT' || r.status === 'sent' ? (
                            <span className="text-xs text-green-600 font-medium">Sent</span>
                          ) : r.status === 'FAILED' ? (
                            <span className="text-xs text-red-600 font-medium">Failed</span>
                          ) : (
                            <span className="text-xs text-muted-foreground capitalize">{r.status?.toLowerCase()}</span>
                          )}
                          {r.opens > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-500/10 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">
                              👁 {r.opens}
                            </span>
                          )}
                          {r.clicks > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] bg-purple-500/10 text-purple-600 px-1.5 py-0.5 rounded-full font-medium">
                              🔗 {r.clicks}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setIsCampaignDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {warmAckInfo && (
        <WarmVolumeAckDialog
          open={warmAckOpen}
          onOpenChange={setWarmAckOpen}
          count={warmAckInfo.count}
          threshold={warmAckInfo.threshold}
          prospectOverride={warmAckInfo.prospectOverride}
          isBusy={warmAckMode === "schedule" ? schedulingMass : sendBulkEmailMutation.isPending}
          onConfirm={() => {
            if (warmAckMode === "schedule") {
              submitScheduleMass(true);
              return;
            }
            setWarmAckOpen(false);
            if (pendingWarmPayload) {
              sendBulkEmailMutation.mutate({ ...pendingWarmPayload, confirmWarmHighVolume: true });
            }
          }}
        />
      )}
    </div>
  );
}
